import { useEffect, useMemo } from 'react';
import { Components } from '@/schemas/sceneDataSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldErrors, ResolverError, ResolverOptions, ResolverResult, useForm, UseFormReturn } from 'react-hook-form';
import { FormValues } from '@/constants/formTypes';
import { FormConfigResolver } from '@/schemas/formConfigResolver';
import { convertToFieldDateType } from '@/schemas/converters';
import { createErrorsFromServer } from '@/components/DynamicUI/createErrorsFromServer';

type ClientResolverResult = ResolverResult<FormValues, FormValues>;
type ClientResolverError = ResolverError<FormValues>;

function hasErrors(errors: ClientResolverResult['errors']): errors is ClientResolverError['errors'] {
  return Object.keys(errors).length !== 0;
}

function toErrors(errors: ClientResolverResult['errors'], exclude: string[] = []): FieldErrors<FormValues> {
  if (hasErrors(errors)) {
    return Object.fromEntries(
      Object.entries(errors).filter(([key]) => !exclude.includes(key))
    ) as FieldErrors<FormValues>;
  } else {
    return {};
  }
}

export default function useDynamicForm(
  components: Components,
  {
    validateOnMount = false,
    validationMode,
  }: { validateOnMount?: boolean; validationMode?: 'onSubmit' | 'onChange' } = {}
): UseFormReturn<FormValues, unknown, FormValues> {
  const formResolver = useMemo(() => new FormConfigResolver(components), [components]);

  const schema = useMemo(() => formResolver.formSchema(), [formResolver]);
  const defaultValues = useMemo(() => formResolver.defaultValues(), [formResolver]);
  const maskedFields = useMemo(() => formResolver.maskedFields(), [formResolver]);
  const serverErrors = useMemo(() => formResolver.invalidFields(), [formResolver]);
  const fields = useMemo(() => formResolver.inputFields, [formResolver]);

  const baseResolver = zodResolver(schema);

  const resolver = async (
    values: FormValues,
    context: unknown,
    options: ResolverOptions<FormValues>
  ): Promise<ClientResolverResult> => {
    // Find the server errors that have not changed
    const unchangedServerErrors = serverErrors.filter((field) => values[field.key] === defaultValues[field.key]);

    // Find the masked fields that have not changed
    const unchangedMaskedFields = maskedFields.filter((field) => values[field] === defaultValues[field]);

    // This handles some of nuance of how values are stored in
    // html inputs and converts them to the desired type
    const convertedValues = convertToFieldDateType(values, fields);
    const result: ClientResolverResult = await baseResolver(convertedValues, context, options);

    // Here we want to ignore server error for unchanged values
    // And ignore client errors that are masked. Both of these are
    // eventually ignored by the system downstream
    const formattedServerErrors = createErrorsFromServer(unchangedServerErrors, options.fields);
    const filteredClientErrors = toErrors(result.errors, unchangedMaskedFields);

    if (hasErrors(formattedServerErrors) || hasErrors(filteredClientErrors)) {
      const errors: FieldErrors<FormValues> = {
        ...toErrors(formattedServerErrors),
        ...filteredClientErrors,
      };

      // failure case we encountered an error on the client
      // or the server
      return {
        errors,
        values: {},
      };
    } else {
      // success case we shouldn't have errors
      return {
        values: convertedValues,
        errors: {},
      };
    }
  };

  const form = useForm({
    defaultValues,
    resolver,
    // Explicit validation_mode from server wins; otherwise validate_on_mount
    // forms default to onChange so errors clear as the user fills fields,
    // and other forms keep onSubmit (errors only show after submit).
    mode: validationMode ?? (validateOnMount ? 'onChange' : 'onSubmit'),
  });

  useEffect(() => {
    if (validateOnMount) {
      void form.trigger();
      return;
    }
    if (serverErrors.length === 0) return;
    void form.trigger();
  }, [form, serverErrors, validateOnMount]);

  return form;
}
